/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, CreditCard, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { walletApi, gbp } from "@/lib/wallet";
import SEO from "@/components/site/SEO";

export default function CustomerTopUp() {
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await walletApi.get("/customer/payments/packages");
        setPackages(data.packages || []);
        // Default-select the £10 package if present, else first
        const def = data.packages?.find((p) => p.id === "gbp_10") || data.packages?.[0];
        if (def) setSelected(def.id);
      } catch {
        toast.error("Couldn't load top-up packages");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function startCheckout() {
    if (!selected) return;
    setPaying(true);
    setError("");
    try {
      const { data } = await walletApi.post("/customer/payments/checkout", {
        package_id: selected,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url; // hand off to Stripe
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Could not start checkout";
      setError(typeof msg === "string" ? msg : "Could not start checkout");
      setPaying(false);
    }
  }

  return (
    <div className="min-h-screen bg-brand-bgSoft text-brand-ink" data-testid="customer-topup">
      <SEO title="Top up wallet" path="/app/wallet/topup" />
      <header className="bg-white border-b border-brand-line">
        <div className="container-page flex items-center justify-between h-16">
          <Link to="/app/wallet" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ink/80 hover:text-brand-ink" data-testid="topup-back">
            <ArrowLeft className="h-4 w-4" /> Back to wallet
          </Link>
        </div>
      </header>

      <div className="container-page py-10 max-w-3xl">
        <p className="label-eyebrow text-brand-green">Top up</p>
        <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl">Add credits to your wallet</h1>
        <p className="mt-2 text-brand-muted text-sm max-w-prose">
          Pick a top-up amount. We'll hand you off to Stripe for secure card payment. Funds land in your closed-loop GBP wallet the moment Stripe confirms.
        </p>

        {loading ? (
          <div className="mt-8 text-sm text-brand-muted">Loading packages…</div>
        ) : (
          <>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="topup-packages">
              {packages.map((p) => {
                const isSel = selected === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelected(p.id)}
                    data-testid={`topup-pkg-${p.id}`}
                    className={`relative rounded-2xl border p-5 text-left transition focus:outline-none focus:ring-2 focus:ring-brand-green
                      ${isSel ? "border-brand-green ring-2 ring-brand-green/40 bg-white shadow" : "border-brand-line bg-white hover:border-brand-green/50"}`}
                  >
                    <p className="text-xs font-semibold text-brand-muted uppercase tracking-wider">Top up</p>
                    <p className="mt-2 font-display font-black text-2xl">{p.label}</p>
                    {p.id === "gbp_10" && (
                      <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-bold text-brand-greenDark bg-brand-greenSoft rounded-full px-2 py-0.5">
                        <Sparkles className="h-3 w-3" /> Popular
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {error && (
              <div className="mt-6 flex items-start gap-2 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3" data-testid="topup-error">
                <AlertCircle className="h-4 w-4 mt-0.5" /> <span>{error}</span>
              </div>
            )}

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={startCheckout}
                disabled={!selected || paying}
                data-testid="topup-pay-btn"
                className="inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover disabled:opacity-60 px-6 py-3 text-base font-semibold text-white shadow-sm transition"
              >
                <CreditCard className="h-5 w-5" />
                {paying ? "Redirecting to Stripe…" : `Pay ${packages.find((p) => p.id === selected)?.label || ""} with card`}
              </button>
              <p className="text-xs text-brand-muted">Powered by Stripe · Cards, Apple Pay & Google Pay</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
